// Rulebook v3 — the eleven text pages (written from docs/porosity-v3-spec.md).
// Uses the same card names, numbers and icons as the game.
import Coin from "@/components/game/Coin";
import { ArtCrop } from "@/components/game/CardArt";
import { STAT_META } from "@/components/game/CardStats";
import { WeatherIcon, FACE_VISUALS } from "@/components/game/WeatherDice";
import { weatherCardSrc, globalCardSrc, cardBackSrc } from "@/lib/artAssets";

const STAT = Object.fromEntries(STAT_META.map((m) => [m.key, m]));

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
function H({ children }) {
  return <h4 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", margin: "22px 0 8px" }}>{children}</h4>;
}
function P({ children, muted = false }) {
  return <p style={{ fontSize: 15, lineHeight: 1.6, color: muted ? "var(--ink-4)" : "var(--ink-2)", margin: "0 0 10px" }}>{children}</p>;
}
function UL({ children }) {
  return <ul style={{ margin: "0 0 10px", paddingLeft: 20, listStyle: "disc", fontSize: 15, lineHeight: 1.6, color: "var(--ink-2)" }}>{children}</ul>;
}
function LI({ children }) {
  return <li style={{ marginBottom: 4 }}>{children}</li>;
}
function B({ children }) {
  return <strong style={{ fontWeight: 700, color: "var(--ink)" }}>{children}</strong>;
}
function Box({ title, children, tone = "sea" }) {
  const c = tone === "danger" ? "var(--danger)" : "var(--sea)";
  return (
    <div style={{ border: "1px solid var(--line)", borderLeft: `3px solid ${c}`, borderRadius: 10, padding: "12px 16px", background: "var(--surface)", margin: "12px 0" }}>
      {title && <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: c, marginBottom: 6 }}>{title}</div>}
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: "var(--ink-2)" }}>{children}</div>
    </div>
  );
}
function StatIcon({ k, size = 15 }) {
  const m = STAT[k];
  return (
    <span className="inline-flex items-center align-middle" title={m.label} aria-label={m.label} style={{ verticalAlign: "-2px" }}>
      <m.Icon s={size} c={m.color} />
    </span>
  );
}
function Stat({ k, children }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <StatIcon k={k} />
      <span>{children || STAT[k].label}</span>
    </span>
  );
}
function Money({ n }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" style={{ verticalAlign: "-2px" }}>
      <Coin size={14} />
      <span style={{ fontWeight: 600 }}>{n}</span>
    </span>
  );
}

const TH = { textAlign: "left", fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--ink-4)", padding: "8px 8px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" };
const TD = { fontSize: 14, lineHeight: 1.45, color: "var(--ink-2)", padding: "8px 8px", borderBottom: "1px solid var(--line-soft)", verticalAlign: "middle" };
function Table({ head, rows, testid, align = [] }) {
  return (
    <div className="overflow-x-auto" style={{ margin: "8px 0 12px" }}>
      <table data-testid={testid} className="w-full" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} style={{ ...TH, textAlign: align[i] || "left" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} style={{ ...TD, textAlign: align[ci] || "left" }}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card data (defaults from the spec — same as the game)
// ---------------------------------------------------------------------------
const BUILD_ROWS = [
  { key: "house_cube", carbon: "−1 per floor", name: "House Cube", type: "Building", cost: 2, s: [0, 0, 0, "1*"], where: "An empty square, or on top of your own building. Up to 3 floors. +1 population per floor, +1 more on a coastal square." },
  { key: "urban_park", carbon: "+1", name: "Urban Park", type: "Nature tile", cost: 2, s: [1, 2, 0, 0], where: "An empty square. +1 population once if it is next to one of your buildings." },
  { key: "urban_forest", carbon: "+3", name: "Urban Forest", type: "Nature tile", cost: 5, s: [2, 3, 0, 0], where: "An empty square." },
  { key: "inland_wetland", carbon: "+3", name: "Inland Wetland", type: "Nature tile", cost: 4, s: [3, 1, 0, 0], where: "An empty square, never a coastal square." },
  { key: "mangrove_forest", carbon: "+5", name: "Mangrove Forest", type: "Nature tile", cost: 5, s: [2, 2, 2, 0], where: "Any empty coastal square. The sea needs 2 more levels to reach it. No walls on it." },
  { key: "green_roof", carbon: "0", name: "Green Roof", type: "Upgrade · top", cost: 1, s: [1, 1, 0, 0], where: "On your own building, 1 per stack. The building is no longer grey." },
  { key: "green_street", carbon: "0", name: "Green Street", type: "Upgrade · edge", cost: 1, s: [1, 1, 0, 0], where: "On the square of one of your buildings, 1 per square. The building is no longer grey, and your green tiles around it link up." },
  { key: "low_sea_wall", carbon: "−1", name: "Low Sea Wall", type: "Upgrade · wall", cost: 2, s: [0, 0, 1, 0], where: "One wall on one edge of your square. The sea can’t pass it. Population −1, storm +1." },
];
const STAT_ORDER = ["absorb", "shade", "storm", "pop"];

const ACTION_ROWS = [
  { key: "sabotage", name: "Sabotage", copies: 3, when: "Your turn", effect: "Break one opponent’s Sea Wall; the sea comes in at once." },
  { key: "repair_card", name: "Repair Card", copies: 3, when: "Your turn", effect: "Fix a sabotaged wall; squares it flooded come back if the sea hasn’t risen since." },
  { key: "relocated", name: "Relocated", copies: 4, when: "Your turn", effect: "Move one of your squares, with everything on it, to any empty square." },
  { key: "board_thief", name: "Board Thief", copies: 2, when: "Your turn", effect: "Swap the ownership of one of your squares with one of an opponent’s squares (everything on them changes owner; nothing moves). The opponent may block it with a Protection Card." },
  { key: "protection_card", name: "Protection Card", copies: 4, when: "Response", effect: "Response: cancel your weather repairs, or block a Board Thief played on you. Not against sea level rise." },
];

function CardName({ k, name }) {
  return (
    <span className="inline-flex items-center gap-2" style={{ fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap" }}>
      <ArtCrop card={{ key: k }} size={30} radius={5} />
      <span>{name}</span>
    </span>
  );
}

const WEATHER_ROWS = [
  { key: "clear_skies", name: "Clear Skies", cards: 4, area: "—", need: "Nothing happens", stat: "—" },
  { key: "light_rain", name: "Light Rain", cards: 3, area: "A random shape of 4 connected squares", need: "1", stat: "absorb" },
  { key: "heavy_rain", name: "Heavy Rain", cards: 5, area: "A random shape of 6 connected squares", need: "2", stat: "absorb" },
  { key: "heat_wave", name: "Heat Wave", cards: 3, area: "A random shape of 4 connected squares", need: "1", stat: "shade" },
  { key: "heat_storm", name: "Heat Storm", cards: 2, area: "A random shape of 6 connected squares", need: "2", stat: "shade" },
  { key: "storm_surge", name: "Storm Surge", cards: 3, area: "The squares the sea reaches when it stands 1 level higher, for this round only", need: "2 minus the square’s storm protection", stat: "storm" },
];

function FaceBadge({ k }) {
  const v = FACE_VISUALS[k];
  return (
    <span className="inline-flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 7, background: v.bg, color: v.fg }}>
      <WeatherIcon face={k} size={24} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------
function Overview() {
  return (
    <>
      <P>
        Porosity is a game for <B>2, 3 or 4 players</B>. Together you build one shared city by the sea, but every square belongs to one player. Houses grow your
        population; parks, forests, wetlands and mangroves give shade, soak up rain and hold back storms.
      </P>
      <P>
        Each round the weather tests your part of the city. And every House Cube floor and Sea Wall anyone builds puts carbon on a shared meter: every 10 carbon
        the planet warms by 1° and the sea rises. The meter never goes down. Green tiles and the Climate Bank slow it, so the game lasts longer.
      </P>
      <Box title="Goal">
        The game ends when the world reaches <B>3°</B> — then the player with the <B>most resilience</B> wins — or after <B>round 12</B>, when the highest
        <B> population + resilience − debt</B> wins. Money is not scored — it’s there to be spent.
      </Box>
      <H>Words you’ll see</H>
      <UL>
        <LI><B>Square</B> — one space of the board. Your squares are the ones with your pieces on them.</LI>
        <LI><B>Coastal square</B> — a dry square in the bottom row or next to a flooded square. When the sea rises and floods squares, the squares next to them become coastal.</LI>
        <LI><B>Building</B> — a stack of House Cubes on one square.</LI>
        <LI><B>Nature tile</B> — Urban Park, Urban Forest, Inland Wetland or Mangrove Forest.</LI>
        <LI><B>Upgrade</B> — a token placed on your square: Green Roof on top of a building, a Green Street, or a Sea Wall (one wall on one edge of the square).</LI>
        <LI><B>Market</B> — 4 face-up build cards everyone takes from.</LI>
        <LI><B>Bank</B> — where money is paid to and paid from (not the shared Climate Bank, which is a pot the players fill together). <Money n="" /> is money.</LI>
        <LI><B>Debt</B> — what you owe when you can’t pay. It’s paid back first from your income.</LI>
      </UL>
      <H>A round at a glance</H>
      <UL>
        <LI>Everyone takes one turn (in reverse seat order — see “Your turn”).</LI>
        <LI>End of the round, always in this order: (1) a weather card is drawn and its weather hits the board; (2) the global card drawn with it counts; (3) the world adds +1 carbon; (4) the meter is updated; (5) the sea rises once for every new degree reached, and the weather deck loses its mildest cards; (6) everyone gets income.</LI>
        <LI>Then the next round begins — unless the game has ended.</LI>
      </UL>
    </>
  );
}

function Setup() {
  return (
    <>
      <Table
        testid="rulebook-board-table"
        head={["Players", "Board", "Sea"]}
        rows={[
          ["2 players", "5 columns × 5 rows", "Along the bottom edge"],
          ["3 players", "5 columns × 6 rows", "Along the bottom edge"],
          ["4 players", "6 columns × 7 rows", "Along the bottom edge"],
        ]}
      />
      <H>Terrain heights</H>
      <P>
        Every square has a <B>terrain height</B> from <B>▲1</B> (low) to <B>▲4</B> (high), shown in its top-left corner and by its colour (the higher, the darker).
        The heights are set when the game starts and never change. The sea itself is height 0.
      </P>
      <Table
        testid="rulebook-terrain-table"
        head={["Players", "▲1", "▲2", "▲3", "▲4"]}
        rows={[
          ["2 players", "5", "5", "5", "10"],
          ["3 players", "6", "6", "6", "12"],
          ["4 players", "8", "8", "8", "18"],
        ]}
      />
      <UL>
        <LI>The land is lower near the coast: squares close to the sea are more likely to be ▲1 or ▲2, inland squares ▲3 or ▲4.</LI>
        <LI>At least 2 squares of the bottom (coast) row are always ▲1.</LI>
        <LI>Tap a square with a piece on it to read its height in the card view (“Height ▲2”).</LI>
      </UL>
      <H>1. Money</H>
      <P>Everyone starts with <Money n={8} />.</P>
      <H>2. Starting House Cubes</H>
      <P>
        Each player places <B>2 starting House Cubes</B>, in seat order (Player 1, Player 2, then Player 3 and Player 4 if they are playing), <B>one cube at a time</B>:
        everyone places one, then everyone places their second. Put a cube on an empty square or on top of your own cube.
      </P>
      <UL>
        <LI>Starting cubes are free.</LI>
        <LI>Starting cubes carry <B>no carbon</B>: they add nothing to the meter.</LI>
        <LI>Short on time? “Randomize setup” places everyone’s starting cubes for you.</LI>
      </UL>
      <H>3. Cards</H>
      <UL>
        <LI>Shuffle the build deck and deal each player <B>4 build cards</B> — your hand.</LI>
        <LI>Turn <B>4 build cards</B> face up: this is the market.</LI>
        <LI>Shuffle the action pile and keep it face down, separate from the build deck.</LI>
        <LI>Shuffle the weather deck (20 cards) and the global deck (24 cards); the carbon meter and the Climate Bank start at 0.</LI>
      </UL>
      <H>The decks</H>
      <Table
        testid="rulebook-deck-table"
        head={["Deck", "Cards", "Total"]}
        rows={[
          ["Build deck, 2 players", "34 House Cubes · 10 Low Sea Walls · 5 Urban Parks · 4 Urban Forests · 5 Inland Wetlands · 4 Mangrove Forests · 5 Green Roofs · 5 Green Streets", "72"],
          ["Build deck, 3 players", "42 House Cubes · 14 Low Sea Walls · 7 Urban Parks · 6 Urban Forests · 7 Inland Wetlands · 6 Mangrove Forests · 7 Green Roofs · 7 Green Streets", "96"],
          ["Build deck, 4 players", "53 House Cubes · 17 Low Sea Walls · 9 Urban Parks · 7 Urban Forests · 9 Inland Wetlands · 7 Mangrove Forests · 9 Green Roofs · 9 Green Streets", "120"],
          ["Action pile", "Sabotage 3 · Board Thief 2 · Relocated 4 · Protection Card 4 · Repair Card 3", "16"],
        ]}
      />
      <P muted>The action pile is the same for 2, 3 and 4 players.</P>
    </>
  );
}

function Cards() {
  return (
    <>
      <P>
        Every build card has four stats, from 0 to 3. The first three protect your city from weather, the last one is population.
      </P>
      <div className="flex flex-wrap gap-x-5 gap-y-2" style={{ fontSize: 14, color: "var(--ink-2)", margin: "4px 0 10px" }} data-testid="rulebook-stat-legend">
        <Stat k="absorb" />
        <Stat k="shade" />
        <Stat k="storm" />
        <span style={{ color: "var(--line)" }}>|</span>
        <Stat k="pop" />
      </div>
      <H>Build cards</H>
      <Table
        testid="rulebook-build-table"
        align={["left", "left", "center", "center", "center", "center", "center", "center", "left"]}
        head={[
          "Card",
          "Type",
          <Coin key="c" size={14} />,
          ...STAT_ORDER.map((k) => <StatIcon key={k} k={k} size={15} />),
          "Carbon",
          "Where it goes",
        ]}
        rows={BUILD_ROWS.map((r) => [
          <CardName key="n" k={r.key} name={r.name} />,
          <span key="t" style={{ whiteSpace: "nowrap" }}>{r.type}</span>,
          r.cost,
          ...r.s.map((v, i) => (
            <span key={i} style={{ color: v === 0 ? "var(--ink-5)" : "var(--ink)", fontWeight: v === 0 ? 400 : 600 }}>{v}</span>
          )),
          <span key="cb" style={{ whiteSpace: "nowrap" }}>{r.carbon}</span>,
          r.where,
        ])}
      />
      <P muted>* A House Cube gives 1 population per cube in the stack. On their own, House Cubes have no other stats — add upgrades to protect them. Carbon: + is stored by a green tile in the city sink when you place it; − is emitted onto the meter when you place it (see “Carbon meter & sea level”).</P>
      <UL>
        <LI>Each square holds at most <B>1 top token</B> (Green Roof, on a building), <B>1 Green Street</B> (on a building) and up to <B>4 Sea Walls</B> (one per edge) — a square may have a Green Street and walls.</LI>
        <LI><B>Sea Walls go on edges</B>: each Low Sea Wall card (<Money n={2} />) is one wall on one free edge of one of your squares — a building or a nature tile, not a Mangrove; never an empty square, the sea or an opponent’s square. Select the card, click your square, then click one of its 4 edge outlines (an edge that already has a wall is greyed with the reason).</LI>
        <LI>A building square shows a street around it; a Green Street turns its sidewalks green. Tokens never move a building or change its height.</LI>
      </UL>
      <H>Action cards</H>
      <P>Every action card costs <Money n={3} />, paid when you take it from the action card deck. Playing it later is free.</P>
      <Table
        testid="rulebook-action-table"
        align={["left", "center", "left", "left"]}
        head={["Card", "Copies", "When", "What it does"]}
        rows={ACTION_ROWS.map((r) => [
          <CardName key="n" k={r.key} name={r.name} />,
          r.copies,
          <span key="w" style={{ whiteSpace: "nowrap" }}>{r.when}</span>,
          r.effect,
        ])}
      />
    </>
  );
}

function Turn() {
  return (
    <>
      <H>Turn order</H>
      <P>
        Turns go in <B>reverse seat order</B>, and the first player moves one seat down each round:
      </P>
      <Table
        testid="rulebook-turn-order-table"
        head={["", "Round 1", "Round 2", "Round 3", "Round 4"]}
        rows={[
          ["2 players", "2, 1", "1, 2", "2, 1 (repeats)", "1, 2"],
          ["3 players", "3, 2, 1", "2, 1, 3", "1, 3, 2", "3, 2, 1 (repeats)"],
          ["4 players", "4, 3, 2, 1", "3, 2, 1, 4", "2, 1, 4, 3", "1, 4, 3, 2"],
        ]}
      />
      <H>On your turn</H>
      <P>Do any of these, in any order, then press <B>End turn</B>:</P>
      <UL>
        <LI>
          <B>Play build cards</B> — one at a time, as many as you like. Playing is <B>free</B>: you paid for the card when you took it. Place the card, then
          take 1 card (and pay for it). You may then play again. Your starting hand is free.
        </LI>
        <LI>
          <B>Take an action card</B> — once per turn, for <Money n={3} /> paid at once (you need the <Money n={3} />): the top card of the action pile, if you hold
          fewer than 3. Action cards lie face down beside you, hidden from the others (hold up to 3).
        </LI>
        <LI>
          <B>Play an action card</B> — at most 1 per turn, free (it was paid when taken). The Protection Card is a response only: you play it
          when the weather hits or a Board Thief targets you — also free.
        </LI>
        <LI><B>Discard</B> — at most 1 build card per turn (no refund), then take 1 card (market or deck).</LI>
        <LI><B>Discard an action card</B> — also in debt, no refund, no replacement card: at most 2 per game.</LI>
        <LI><B>Demolish</B> — at most 1 per turn, for a flat <Money n={2} />: clear one of your squares.</LI>
        <LI><B>Climate Bank</B> — pay coins in or propose one project (not while you are taking a card; paying in not in debt). See “Climate Bank”.</LI>
      </UL>
      <H>The market</H>
      <UL>
        <LI>After you play or discard a build card, take one of the 4 face-up market cards — <B>pay its price at once</B> — or draw the top card of the build deck blind: only you see it, then you pay its price; if you can’t, it goes to the discard pile (never into debt).</LI>
        <LI>Taking a card never puts you into debt. If you can’t afford any market card you may skip taking one (you may still try the blind draw).</LI>
        <LI>A card taken from the market is replaced at once from the build deck. If the deck is empty, shuffle the build discard pile into a new deck. If both are empty, the market stays short.</LI>
        <LI><B>Wipe</B>: once per turn, pay <Money n={2} /> to replace all 4 market cards (on your turn or while picking a card).</LI>
        <LI>If 3 or more of the 4 face-up cards are the same card, the market is wiped automatically for free.</LI>
      </UL>
      <Box title="In debt?" tone="danger">
        While you’re in debt you can’t take build or action cards (skip the take after a play or discard), wipe the market or demolish. The cards already in
        your hand are paid for: you can still play them (response cards too), discard and end your turn.
      </Box>
    </>
  );
}

function Economy() {
  return (
    <>
      <H>Population</H>
      <P>
        <Stat k="pop">Population</Stat> = the House Cubes you own on the board <B>+ (n − 1)</B> for each group of <B>n ≥ 2</B> of your buildings that touch
        side by side, <B>+ 1 for every House Cube on a coastal square</B>, <B>+ 1 for every Urban Park of yours that touches one of your buildings</B> (once per park), <B>− 1 for every Sea Wall</B> you own (never below 0). Population counts live — it
        changes the moment you build, and a square that becomes coastal when the sea rises gets the coastal bonus from then on.
      </P>
      <P>A building is at most <B>3 floors</B> high, on every square.</P>
      <Box title="Example">
        You own a 2-cube building next to a 1-cube building, plus a lone 1-cube building elsewhere. That’s 4 cubes, and the two touching buildings form a
        group of 2 (+1). Population = 4 + 1 = <B>5</B>. If the 2-cube building stands on a coastal square, it adds +2 more: <B>7</B>.
      </Box>
      <H>City size</H>
      <P>Your population sets your city size, which decides your income and how expensive repairs are:</P>
      <Table
        testid="rulebook-city-size-table"
        align={["left", "center", "center", "center"]}
        head={["City size", "Population", "Income per round", "Repairs ×"]}
        rows={[
          ["Small", "0–4", <Money key="m" n={4} />, "×1"],
          ["Medium", "5–9", <Money key="m" n={6} />, "×1"],
          ["Big", "10+", <Money key="m" n={8} />, "×2"],
        ]}
      />
      <H>Income</H>
      <UL>
        <LI>Income is paid at the <B>end of every round</B> (the last one too), after the weather, the global card and any sea rise.</LI>
        <LI>You get your <B>city-size income</B> (Small <Money n={4} />, Medium <Money n={6} />, Big <Money n={8} />) — nothing else.</LI>
        <LI>If you’re in debt, your income pays off the debt first.</LI>
      </UL>
      <H>Repairs and debt</H>
      <UL>
        <LI>Repairs = what the weather needed but your city couldn’t supply, <B>× your repairs multiplier</B>. You pay them to the bank.</LI>
        <LI>If you can’t pay, the rest becomes debt.</LI>
        <LI>Money is <B>not</B> scored. Every unit of debt left at the end scores <B>−1</B>.</LI>
      </UL>
    </>
  );
}

function Weather() {
  return (
    <>
      <div className="flex flex-wrap items-center" style={{ gap: 20 }}>
        <div className="shrink-0 flex" style={{ gap: 10 }} data-testid="rulebook-weather-cards">
          <img decoding="async" loading="lazy" src={cardBackSrc("weather")} alt="Weather card" draggable={false} style={{ width: 92, borderRadius: 7 }} />
          <img decoding="async" loading="lazy" src={weatherCardSrc("heavy_rain")} alt="Heavy Rain card" draggable={false} style={{ width: 92, borderRadius: 7 }} />
        </div>
        <div className="flex-1" style={{ minWidth: 220 }}>
        <P>
          At the end of every round, as soon as the last player ends their turn, <B>the app draws the top card of the weather deck</B>; nobody presses anything, picks the result or a target.
          The deck has <B>20 cards</B>: Clear Skies 4, Light Rain 3, Heavy Rain 5, Heat Wave 3, Heat Storm 2, Storm Surge 3. A drawn card goes to the discard pile; when the deck is empty the discard is shuffled into a new deck.
        </P>
        <P><B>The weather gets worse as the planet warms.</B> When the meter reaches 1°, 2 Clear Skies and 1 Light Rain leave the deck for good (17 cards); at 2°, 1 Clear Skies, 1 Light Rain and 1 Heavy Rain more (14 cards). Cards are never added back.</P>
        <P>The weather card and then the global card turn over in the middle of every screen. For rain and heat the area shuffles across the board before it lands; a Storm Surge shows the squares the water reaches. Results show on the left, and a few seconds later the next round starts by itself. Clear Skies: nothing happens this round.</P>
        </div>
      </div>
      <Table
        testid="rulebook-weather-table"
        align={["left", "center", "left", "left", "left"]}
        head={["Card", "In the deck", "Area", "Need per square of yours", "Tests"]}
        rows={WEATHER_ROWS.map((r) => [
          <span key="n" className="inline-flex items-center gap-2" style={{ fontWeight: 600, color: "var(--ink)" }}>
            <FaceBadge k={r.key} />
            {r.name}
          </span>,
          <span key="sl" className="tabular-nums" style={{ whiteSpace: "nowrap" }}>{r.cards} of 20</span>,
          r.area,
          r.need,
          r.stat === "—" ? "—" : <Stat key="s" k={r.stat} />,
        ])}
      />
      <P>Rain and heat areas land at a random spot on land squares; you'll see them sweep across the board and then stop.</P>
      <H>Who pays?</H>
      <P>Each player is checked on their own:</P>
      <UL>
        <LI>
          <B>Need</B> = the need per square × the number of your squares inside the area. Every square you own counts — buildings and nature alike.
        </LI>
        <LI>
          <B>Supply</B> = the tested stat of your pieces on squares inside the area <B>and one step outside it</B> (up, down, left, right). A square’s stat is
          its tile plus its tokens; House Cubes only count through their upgrades. For rain and heat, each of your nature groups with a tile in that reach adds
          its group bonus (n − 1) once.
        </LI>
        <LI>
          <B>Storm Surge</B>: the sea stands <B>one level higher than its permanent height, for this round only</B>. The water spreads like a flood: it enters through edges
          without a wall, a square floods if the water level reaches its height, and a Mangrove makes its square 2 levels higher. Nothing is destroyed and the sea level does not change.
          Every one of your squares it reaches costs <B>2 minus that square’s <Stat k="storm" /></B> in repairs (never below 0; a well-protected square never cancels another square’s cost), × your repairs multiplier. A Mangrove has storm 2 and each wall built on a square adds +1. Spare storm protection earns nothing.
        </LI>
      </UL>
      <Box title="Repairs">
        Repairs = (need − supply, never below 0; for Storm Surge, added up square by square) × your repairs multiplier. Pay the bank; whatever you can’t pay becomes debt. Everything resolves at once — no
        water stays on the board.
      </Box>
      <Box title="Spare pays">
        For rain and heat (not Storm Surge): if you have a square inside the area and more than the area needs, the bank pays you the spare, at most <Money n={2} /> per event (no city-size
        multiplier). No square inside the area: you pay nothing and earn nothing.
      </Box>
      <Box title="Example">
        Heavy Rain covers a shape of 6 connected squares with 2 of your squares inside: need = 2 × 2 = 4. Inside or one step outside the area you have an Urban Park (
        <Stat k="absorb">1</Stat>) and an Inland Wetland (<Stat k="absorb">3</Stat>) that touch: supply = 1 + 3 + 1 group bonus = 5. No repairs, and 1 spare: you earn <Money n={1} />.
      </Box>
      <H>Protection Card</H>
      <P>
        When a weather event hits your squares, you may respond with a Protection Card (free to play — you paid <Money n={3} /> when you took it — also in debt): you pay no repairs for that
        event. It does not work against a sea level rise. It can also <B>block a Board Thief</B> played on you: when someone confirms a Board
        Thief on your square, you get a private window (“Ana is playing Board Thief on your Urban Forest (row 2, col 3). Play Protection Card to
        stop it?”). Yes: nothing swaps — the thief’s Board Thief is used up and your Protection Card is discarded, at no extra cost. You always get
        this window, with or without the card, so it gives nothing away.
      </P>
      <Box title="Responses are private">
        After every weather result with repairs, <B>everyone who has repairs</B> gets a short private response window at the same time, with or
        without a Protection Card: a holder sees “Play Protection Card to cancel your repairs of 4?”, anyone else “Repairs 4. You have no
        response.” with Continue. The other screens only show “Players are deciding on responses…”. No answer before the timer runs out counts
        as No. A played card is only announced once the window has closed. On one shared device each player taps their own cover
        (“Ana: tap to see your response — others look away”) in turn.
      </Box>
    </>
  );
}

function Sea() {
  return (
    <>
      <H>The carbon meter</H>
      <UL>
        <LI>The <B>GLOBAL TEMP</B> bar at the top of the screen is the carbon meter. <B>10 carbon = 1°</B> (for 2, 3 and 4 players), and the meter <B>only ever goes up</B>. The sea rises when it reaches <B>1°, 2° and 3°</B>; reaching 3° ends the game.</LI>
        <LI>Carbon reaches the meter <B>at once</B>: every House Cube floor and every Sea Wall you build adds 1. Starting cubes, Green Roofs and Green Streets add nothing. The rest of the world adds <B>+1 at the end of every round</B>, round 1 too.</LI>
        <LI>Green tiles store carbon in the <B>city sink</B> when you place them (Urban Park 1, Urban Forest 3, Inland Wetland 3, Mangrove 5). One sink is shared by the whole board. It cancels the city’s emissions (floors and walls) only, never the world’s or a global card’s.</LI>
        <LI>The <B>shared reserve</B> is filled by Climate Bank projects and cooling global cards. It cancels <B>any</B> emission, including the world’s and warming global cards.</LI>
        <LI>A floor or wall emission uses the city sink first, then the shared reserve; what is left goes on the meter.</LI>
        <LI>Flooding, moving or stealing a piece never changes the meter. <B>Demolishing your own green tile releases its carbon</B> onto the meter (nothing cancels it, and the sink keeps what it stored); demolishing a building or a wall gives nothing back.</LI>
        <LI>After <B>Clean Energy Transition</B> (Climate Bank), floors built from then on emit 0. Walls still emit.</LI>
      </UL>
      <P>Hover over the bar to see this round’s totals and the last global card. The two small gauges show the city sink (green) and the shared reserve (blue).</P>
      <H>When the sea rises</H>
      <UL>
        <LI>A rise happens at the <B>end of the round</B> in which a degree was reached, <B>after</B> that round’s weather, global card and the world’s +1. It never replaces the weather.</LI>
        <LI>If two degrees are reached in one round, <B>both</B> rises happen that round.</LI>
        <LI>Each degree also removes cards from the weather deck (see “Weather deck”).</LI>
      </UL>
      <H>How the sea floods</H>
      <UL>
        <LI>The <B>sea level</B> is 0 at the start; the 1st rise makes it 1, the 2nd 2, the 3rd 3 (the bar under the board shows “Sea level 1”).</LI>
        <LI>Rows are counted from the <B>original coastline</B> and never change: the first row of land (the bottom row) is <B>row 0</B>, then row 1, 2, 3, 4; columns are numbered 1, 2, 3… from the left. Sentences and the game log use “row 2, col 3”; the board’s corner labels show each square’s terrain height (“▲2”).</LI>
        <LI>A square’s <B>flood height</B> = its terrain height (▲1–▲4), <B>+2 with a Mangrove Forest</B> on it. Sea Walls add no height: they close edges.</LI>
        <LI>Water enters a dry square through one of its <B>four edges</B> (never diagonally): its bottom edge if it is in the bottom row (from the sea), or an edge it shares with a flooded square. A working <B>Sea Wall</B> on that edge stops it.</LI>
        <LI>At each rise every dry square whose flood height is <B>at or below the new sea level</B> floods if water can get in — again and again, so water <B>spreads through connected low ground</B>. A low square cut off by higher ground or by walls stays dry.</LI>
        <LI>Flooded squares stay sea for the rest of the game. Everything on them is lost. Walls on their edges stay — a wall on a shared edge still protects the dry square next to it.</LI>
        <LI>A <B>coastal square</B> is a dry square in the bottom row or next to a flooded square; coastal squares move inland as the sea rises (coastal population bonus, Mangroves, Inland Wetlands).</LI>
      </UL>
      <H>Sea Walls on edges</H>
      <Table
        testid="rulebook-walls-table"
        align={["left", "left"]}
        head={["Situation", "What happens"]}
        rows={[
          [<CardName key="n" k="low_sea_wall" name="One wall" />, "One Sea Wall card = one wall on one edge (top, bottom, left or right) of your own square. Height 3: the sea never goes over it."],
          [<CardName key="n" k="low_sea_wall" name="Every way in" />, "A low square needs a wall on every edge the water can come through. A ▲1 bottom-row square next to a flooded square needs its bottom edge and that shared edge walled."],
          [<CardName key="n" k="low_sea_wall" name="Shared edge" />, "One wall per edge. A wall on an edge two squares share belongs to whoever built it and protects both."],
          [<CardName key="n" k="low_sea_wall" name="Up to 4" />, "Up to 4 walls per square (one per edge), never on a Mangrove. You may wall an edge the water can’t reach yet."],
        ]}
      />
      <UL>
        <LI>Each wall: <B>population −1</B> for its owner (a broken one too) and <B>storm protection +1</B> for its square (a broken one gives 0).</LI>
        <LI>Walls stay with the land: they stay when the piece on the square is relocated or demolished, and pass to the new owner when Board Thief swaps that square.</LI>
        <LI><B>Sabotage</B> breaks one opponent wall (pick the square, then the edge): it works as no wall and the water comes in at once at the current sea level. A <B>Repair Card</B> makes it work again; if the sea hasn’t risen since, the squares that flooded because of it come back dry with everything that was on them.</LI>
        <LI>On the board, a <B>hatched</B> square is held back by Sea Walls (the sea would be there without them); a <B>dashed outline</B> marks a square with a building that will flood at the next rise with the walls as they are now.</LI>
        <LI><B>Mangrove Forest</B>: +2 flood height, no walls on it. A Mangrove the sea still reaches is lost like any other piece.</LI>
      </UL>
      <Box title="The end is near" tone="danger">
        When the meter reaches <B>3°</B> the 3rd sea level rise happens, that round is finished, and the game ends. <B>Most resilience wins.</B>
      </Box>
    </>
  );
}

function Bank() {
  return (
    <>
      <P>
        The <B>Climate Bank</B> is a shared pot of coins. It buys three one-off projects, and <B>every player must agree</B> to each one. It is a tab beside the Global deck; click it to open.
      </P>
      <H>Paying in</H>
      <UL>
        <LI>On <B>your own turn</B>, in the free part of it, pay any whole amount up to your money — as often as you like. Not while you are in debt.</LI>
        <LI>No refunds and no personal points. The game log names who paid. Money left in the bank at the end of the game is lost.</LI>
      </UL>
      <H>Proposing and voting</H>
      <UL>
        <LI>The player whose turn it is may propose <B>one project per turn</B>, only if the bank can pay for it right now. Each project can be done <B>once per game</B>; one that was voted down can come back on a later turn.</LI>
        <LI>Every other player answers <B>Yes or No</B> in a private window (online all at once; on one screen one at a time). The proposer counts as Yes. <B>No answer within 60 seconds counts as No.</B></LI>
        <LI>Answers stay secret until the vote ends. Then the log says who agreed and who said no. One No and the project does not happen; the bank keeps its coins.</LI>
      </UL>
      <H>The three projects</H>
      <Table
        testid="rulebook-bank-table"
        align={["left", "left", "left"]}
        head={["Project", "Costs", "What happens"]}
        rows={[
          ["Green Roofs for All", "3 per player", "5 carbon into the shared reserve. Every building standing now without a roof gets a Green Roof. Each roof belongs to the building’s owner and counts for their resilience."],
          ["Living Coastline", "3 per empty coastal square planted", "2 carbon per square into the shared reserve. An unowned Mangrove goes on every empty coastal square (flood height +2, no walls). It counts for nobody’s resilience. Squares with a Sea Wall are skipped; it can’t be proposed if no square qualifies."],
          ["Clean Energy Transition", "6 per player", "House Cube floors built from now on emit no carbon. Sea Walls still do; nothing already emitted comes back."],
        ]}
      />
      <Box title="Why pay in?">
        Everything the bank adds goes to the shared reserve, which cancels emissions of any kind. A longer game gives everyone more rounds to build, so paying in can be in your own interest — but it also helps your rivals.
      </Box>
    </>
  );
}

function Groups() {
  return (
    <>
      <P>
        Squares are connected when they touch <B>side by side</B> (not diagonally). Only your own squares connect to each other, and the sea breaks a group
        apart.
      </P>
      <H>Building groups</H>
      <P>
        A group of <B>n</B> of your buildings that touch adds <B>n − 1</B> to your <Stat k="pop">population</Stat>, live.
      </P>
      <H>Nature groups (weather)</H>
      <P>
        Your nature tiles that touch (Urban Park, Urban Forest, Inland Wetland, Mangrove Forest) form a nature group. Upgrades and tokens don’t link squares.
        A group of <B>n</B> tiles gives <B>n − 1</B>, once per group, to your <Stat k="absorb" /> and <Stat k="shade" /> supply when rain or heat hits and the group has a tile in reach.
      </P>
      <H>Green networks (resilience)</H>
      <P>
        For scoring, your green tiles that touch form a <B>green network</B>; a network of <B>n</B> tiles scores <B>n − 1</B> resilience. A square of yours with a <B>Green Street</B> also links the
        network (so tiles on both sides of a street count as one), but it is not a tile itself. Green Roofs never link. Unowned Mangroves from the Climate Bank join nobody’s network.
      </P>
      <Box title="Example">
        An Urban Park, an Inland Wetland and an Urban Forest in a row form a group of 3: +2 to your supply against rain or heat when the group is in reach, and +2 resilience at the end.
      </Box>
      <P muted>The connectivity view on the board shows your groups.</P>
    </>
  );
}

function End() {
  return (
    <>
      <H>Resilience</H>
      <P>Your resilience is public and shown on your player chip. It is:</P>
      <UL>
        <LI><B>Tiles:</B> the water + shade + storm printed on each of your dry nature tiles — Urban Park 3, Urban Forest 5, Inland Wetland 4, Mangrove Forest 6.</LI>
        <LI><B>Upgrades:</B> Green Roof 2 and Green Street 2 on your buildings.</LI>
        <LI><B>Network:</B> +1 for every green tile joined to another of yours (a network of n tiles gives n − 1). A Green Street square links the network.</LI>
        <LI><B>Grey buildings:</B> −1 for each of your buildings with no Green Roof, no Green Street and none of <B>your own</B> green tiles touching it on a side. An opponent’s tile does not help. A building counts once, however many floors.</LI>
      </UL>
      <P>Sea Walls add nothing. Flooded squares count nothing. A square taken with Board Thief counts for its new owner.</P>
      <H>Two ways to end</H>
      <Box title="1. The world reaches 3°" tone="danger">
        The 3rd sea level rise happens, that round is finished (income is paid), and the game ends. <B>Most resilience wins.</B> Debt is not subtracted. Tie: higher population, then fewest repairs paid, then a shared win.
      </Box>
      <Box title="2. After round 12">
        <span style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>population + resilience − debt</span>
        <div style={{ marginTop: 6 }}>Tie: higher resilience, then fewest repairs paid, then a shared win.</div>
      </Box>
      <UL>
        <LI><Stat k="pop">Population</Stat>: your House Cubes + building group bonuses + 1 per House Cube on a coastal square + 1 per Urban Park touching one of your buildings − 1 per Sea Wall you own (never below 0).</LI>
        <LI>Debt: −1 per unit still owed. Money left over is not scored.</LI>
        <LI>Weather and the global card still happen in round 12, and income is still paid.</LI>
      </UL>
      <Box title="Example">
        Population 9, resilience 7, debt 2 after round 12: 9 + 7 − 2 = <B>14</B>.
      </Box>
      <P muted>Every player’s end-screen row shows population, resilience (tiles, roofs and streets, network, grey buildings), debt and total, and which ending happened.</P>
    </>
  );
}

function Global() {
  return (
    <>
      <div className="flex flex-wrap items-center" style={{ gap: 20 }} data-testid="rulebook-global-cards">
        <div className="shrink-0 flex" style={{ gap: 10 }}>
          <img decoding="async" loading="lazy" src={cardBackSrc("global")} alt="Global card" draggable={false} style={{ width: 92, borderRadius: 7 }} />
          <img decoding="async" loading="lazy" src={globalCardSrc({ no: 1, key: "coal_power_doubles" })} alt="Coal power doubles" draggable={false} style={{ width: 92, borderRadius: 7 }} />
        </div>
        <div className="flex-1" style={{ minWidth: 220 }}>
          <P>
            With every weather card the app draws a <B>global card</B> from its own deck of 24 (the world beyond your city). It shows right after the weather card, and its carbon counts
            once the weather is resolved. Global cards do <B>only</B> carbon: no money, prices or weather.
          </P>
          <UL>
            <LI><B>Warming</B> cards (14) put carbon on the meter. The shared reserve can cancel it; the city sink cannot.</LI>
            <LI><B>Cooling</B> cards (10) put carbon into the shared reserve. The meter itself never goes down; the reserve cancels later emissions.</LI>
          </UL>
        </div>
      </div>
      <H>The 24 cards</H>
      <P><B>Warming (14):</B> Coal power doubles 3 · New oil fields open 3 · Forests cleared for farming 3 · Mangroves cleared for shrimp farms 3 · Wildfire season 2 · Permafrost thaws 2 · Peatlands drained 2 · Cement boom 2 · Fossil-fuel subsidies 2 · Methane leaks 2 · Air travel boom 1 · Highway expansion 1 · Wetlands reclaimed for ports 1 · Food waste 1.</P>
      <P><B>Cooling (10):</B> Solar overtakes coal 3 · Coal plants close 3 · Global forest protection 2 · Mangroves restored abroad 2 · Peatlands rewetted 2 · Climate agreement kept 2 · Green finance 1 · Electric buses 1 · Efficient buildings 1 · Methane deal 1.</P>
      <P>Across the whole deck, warming adds 28 and cooling removes 18, so the deck leans warm. A used card goes to the discard pile; when the deck is empty it is shuffled again.</P>
      <Box title="Order matters">
        The global card counts after the weather, and before the world’s +1. A cooling card therefore fills the shared reserve first, and that reserve can cancel the same round’s world +1.
      </Box>
    </>
  );
}

export const RULEBOOK_PAGE_CONTENT = {
  overview: Overview,
  setup: Setup,
  cards: Cards,
  turn: Turn,
  economy: Economy,
  sea: Sea,
  weather: Weather,
  global: Global,
  bank: Bank,
  groups: Groups,
  end: End,
};
